import { useEffect, useState, useCallback, memo } from 'react'
import { format } from 'date-fns'
import { deleteRecommend, getReviewDetail, postRecommend } from '../../api/product'
import { BtnMore, BtnReviewdLike } from '../../components/Button'
import { IconLiked } from '../../components/svg/IconSvg'
import { useCommonContext } from '../../components/provider/CommonProvider'
import { useMyContext } from '../../components/provider/MyProvider'
import { ProductReviewsItem } from '../../@types/product'

interface ProductReviewItemProps {
  starCountArray: number[]
  el: ProductReviewsItem
}

const imgURL = '/images'

const ProductReviewItem = ({ starCountArray, el }: ProductReviewItemProps) => {
  const [isMore, setIsMore] = useState<boolean>(false)
  const [isLike, setIsLike] = useState<boolean>(false)
  const [checkLike, setCheckLike] = useState<boolean>(false)
  const [likeCount, setLikeCount] = useState<number>(0)
  const [selectedImageSrc, setSelectedImageSrc] = useState<string | null>(null)
  const { setToastMessage, setToastBtnMessage, setToastEvent, setIsToast } = useMyContext()
  const { setIsLogin, hasAccessToken } = useCommonContext()

  const handleImageClick = useCallback((src: string) => {
    setSelectedImageSrc(src)
  }, [])

  const togglePopup = useCallback(() => {
    setSelectedImageSrc(null)
  }, [])

  const formatDate = useCallback((dateString: string) => {
    const reviewDate = new Date(dateString)
    return format(reviewDate, 'yyyy. M. d')
  }, [])

  const recommend = useCallback(async (productNo: string | number, reviewNo: string | number) => {
    try {
      await postRecommend(productNo, reviewNo)
      setCheckLike((prev: boolean) => !prev)
    } catch (err) {
      console.log(err)
    }
  }, [])

  const deleteRecommends = useCallback(async (productNo: string | number, reviewNo: string | number) => {
    try {
      await deleteRecommend(productNo, reviewNo)
      setCheckLike((prev: boolean) => !prev)
    } catch (err) {
      console.log(err)
    }
  }, [])

  const getContentPreview = useCallback((content: string, isMore: boolean) => {
    if (isMore || content.length <= 68) {
      return content
    }
    return `${content.slice(0, 68)}...`
  }, [])

  const handleMoreClick = useCallback(() => {
    setIsMore(!isMore)
  }, [isMore])

  const handleLikeClick = useCallback(() => {
    if (!hasAccessToken) {
      setIsToast(true)
      setToastMessage('로그인이 필요한 서비스입니다.')
      setToastBtnMessage('로그인')
      setToastEvent(() => () => setIsLogin(true))
      return
    }

    if (checkLike) {
      recommend(el.productNo, el.reviewNo)
    } else {
      deleteRecommends(el.productNo, el.reviewNo)
    }
  }, [hasAccessToken, checkLike, el.productNo, el.reviewNo, recommend, deleteRecommends])

  useEffect(() => {
    const fetchReviewDetail = async () => {
      try {
        const res = await getReviewDetail(el.productNo, el.reviewNo)
        setIsLike(res.data.recommendable)
        setLikeCount(res.data.recommendCnt)
      } catch (err) {
        console.log(err)
      }
    }
    fetchReviewDetail()
  }, [checkLike, el.productNo, el.reviewNo])

  useEffect(() => {
    const fetchReviewDetail = async () => {
      try {
        const res = await getReviewDetail(el.productNo, el.reviewNo)
        setCheckLike(res.data.recommendable)
      } catch (err) {
        console.log(err)
      }
    }
    fetchReviewDetail()
  }, [el.productNo, el.reviewNo])

  useEffect(() => {
    setLikeCount(el.recommendCnt)
  }, [el])

  const getStarImageSrc = useCallback((rate: number, idx: number) => {
    const isHalfStar = rate - idx === 0.5
    const isFullStar = idx + 1 <= Math.ceil(rate)

    if (isFullStar) {
      return isHalfStar ? `${imgURL}/star_half_off.svg` : `${imgURL}/star.svg`
    }
    return `${imgURL}/star_off.svg`
  }, [])

  const renderStarRating = useCallback(
    (rate: number, starCountArray: number[]) => {
      return starCountArray.map((_, idx: number) => <img src={getStarImageSrc(rate, idx)} alt='별점' key={idx} />)
    },
    [getStarImageSrc]
  )

  const renderReviewImages = useCallback(
    (fileUrls: string[]) => {
      return fileUrls.map((fileUrl: string) => (
        <a
          key={fileUrl}
          href='#'
          onClick={(e) => {
            e.preventDefault()
            handleImageClick(fileUrl)
          }}
        >
          <div className='review_img' style={{ backgroundImage: `url(${fileUrl})` }}></div>
        </a>
      ))
    },
    [handleImageClick]
  )

  return (
    <>
      <li>
        <div className='review'>
          <div className='review_txt_wrap'>
            <p className='star_rating'>
              {renderStarRating(el.rate, starCountArray)}
              <span className='rating_num'>{el.rate}</span>
            </p>

            <span className='date'>{formatDate(el.updateYmdt)}</span>
          </div>

          <div className='review_contents_wrap'>
            <div className='writer'>{el?.nickname || el?.registerName}</div>

            <div className='review_content'>
              {getContentPreview(el?.content, isMore)}
              {el?.content.length > 68 && !isMore && (
                <BtnMore style={{ marginLeft: '4px' }} ismore={isMore.toString()} onClick={handleMoreClick}>
                  <p>더 보기</p>
                </BtnMore>
              )}
            </div>

            {el.fileUrls?.length !== 0 && <div className='review_image_wrap'>{renderReviewImages(el.fileUrls)}</div>}

            <div className='review_liked'>
              <p>이 리뷰가 도움이 돼요!</p>
              <BtnReviewdLike
                onClick={handleLikeClick}
                islike={`${!isLike}`}
                style={{ border: !isLike ? '1px solid transparent' : '1px solid var(--colGray3)' }}
              >
                <IconLiked islike={isLike} /> {likeCount}
              </BtnReviewdLike>
            </div>
          </div>
        </div>
      </li>
      {selectedImageSrc && (
        <div className='selected_image'>
          <div className='dim'></div>
          <img src={selectedImageSrc} alt='Selected' />
          <button className='close_btn' type='button' onClick={togglePopup}>
            <img src='/images/icon_w_close.png' alt='' />
          </button>
        </div>
      )}
    </>
  )
}

export default memo(ProductReviewItem)
